import { createUseStyles } from 'react-jss';

import { Breakpoints } from '@/lib/breakpoints';
import { HEADER_HEIGHT, BREADCRUMB_HEIGHT, PAGE_GUTTER, PAGE_GUTTER_BOTTOM } from '@/constants/layout';

const useStyles = createUseStyles(
  {
    /**
     * Fills the shell, with a definite height for the embed to size against.
     *
     * `minHeight: 480` was not enough: the miniapp lays itself out with
     * `height: 100%` panels, which resolve to nothing inside an auto-height
     * parent — so its own content overflowed and was clipped at the bottom.
     * `flex: 1` in the page's flex column gives it a real height to fill.
     *
     * `minHeight: 0` because a flex child defaults to `min-height: auto` and
     * would otherwise refuse to shrink below its content, pushing the shell
     * past the viewport instead of letting the embed scroll inside itself.
     */
    container: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',

      /**
       * A viewport-derived height below MD, where nothing above is a flex
       * container.
       *
       * The page only becomes an app shell from MD up — `main` and `PresetBody`
       * get `display: flex` there. Below it they are ordinary blocks, so `flex:
       * 1` means nothing, the slot fell back to its content height, and the
       * miniapp (whose own `height: 100%` needs a parent height) collapsed to
       * zero: visible on desktop, invisible on a phone.
       *
       * `dvh`, not `vh`: mobile browser chrome retracts on scroll, and `vh`
       * measures the tallest state — so the embed would sit under the address
       * bar by exactly its height.
       */
      /**
       * A viewport-derived height below MD, where nothing above is a flex
       * container.
       *
       * The page only becomes an app shell from MD up — `main` and `PresetBody`
       * get `display: flex` there. Below it they are ordinary blocks, so
       * `flex: 1` means nothing and the embed would collapse to zero height.
       *
       * `dvh`, not `vh`: mobile browser chrome retracts on scroll, and `vh`
       * measures the tallest state.
       */
      height: `calc(100dvh - ${HEADER_HEIGHT + BREADCRUMB_HEIGHT + PAGE_GUTTER + PAGE_GUTTER_BOTTOM}px)`,

      // From MD the shell supplies the height; take the track instead.
      [Breakpoints.MD]: { height: 'auto', flex: 1, minHeight: 0 },

      /**
       * Make the relocated miniapp node fill this slot.
       *
       * The miniapp sizes itself with `height: 100%` all the way down
       * (`AppShell` → `AppLayout`), which only resolves against a parent that
       * has a height of its own. The node the runtime hands us has none, so as
       * an ordinary flex child it was content-sized: its panels rendered to
       * their natural height and everything past that was clipped by the card's
       * `overflow: hidden`, leaving the grid cut mid-tile with dead space below.
       *
       * Two levels, because the runtime nests two containers: an outer one it
       * mounts into and an inner one it renders the miniapp inside. Styling only
       * the outer left the inner auto-height, so the miniapp sized to its
       * content — 476px inside an 886px slot, with the rest showing as empty
       * page beneath it.
       *
       * Child selectors because these nodes are not ours — the runtime creates
       * them and `MiniappEmbed` only re-parents them, so there is no
       * className to hang this on.
       */
      /**
       * Absolutely filled, not flexed — copied from the canonical host.
       *
       * `miniapp-shell/shell/index.html` sizes the same nodes as
       * `#miniapp-container > div { position: absolute; inset: 0 }`, and that
       * detail is load-bearing: the runtime is entitled to mount more than one
       * root here (it does exactly that when a nested miniapp such as the Drive
       * chooser closes). As flex children two roots *share* the height, so the
       * live app rendered at half the slot with dead space beneath it. Absolute
       * children each fill the container independently, so a stale root stacks
       * behind the live one and costs no height at all.
       *
       * Two levels, because the runtime nests two containers: an outer one it
       * mounts into and an inner one it renders the miniapp inside.
       */
      position: 'relative',
      '& > *': { position: 'absolute', inset: 0 },
      '& > * > *': { width: '100%', height: '100%' },
    },
  },
  { name: 'miniapp-embed' },
);

export default useStyles;
